import React, { useContext, useEffect, useState } from "react";
import { LAText, LAView } from "../../components";
import { IMAGES, SVG_IMAGES } from "../../assets";
import { STRINGS } from "../../constant";
import { useLocation, useNavigate } from "react-router-dom";
import FlashCardTest from "./Test";
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import BackArrowIcon from "../../components/BackArrowIcon";
import NavBar from "../../components/NavBar";
import BookmarkIcons from "../../components/BookmarkIcons";
import TabFooter from "../../components/TabFooter";
import { getFlashCardsIconBg, getBookMarkColor } from "../../utils/themeUtils";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBookmark,
  faChevronLeft,
  faChevronRight,
  faBook,
  faLayerGroup,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';

const QuickSets = () => {
  const { theme, currentTheme } = useContext(ThemeContext);

  const currentIndex = 0;
  const { state } = useLocation();
  const title = state?.title ?? STRINGS.flashcards;
  const lastIndex = state?.lastIndex ?? 0;
  const set_id = state?.set_id ?? "";

  let cards = state?.cards || [];
  const navigate = useNavigate();
  const {
    profile,
    addFlashcardToBookmark,
    removeFlashcardFromBookmark,
    addFlashcardProgress,
  } = useContext(AuthContext);

  const [activeIndex, setActiveIndex] = useState(lastIndex);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Card animations
  const cardVariants = {
    hover: { 
      scale: 1.02,
      boxShadow: "0px 8px 15px rgba(0,0,0,0.1)",
      transition: { type: "spring", stiffness: 400, damping: 10 }
    }
  };

  // Helper function to get icon background color
  const getIconBg = () => {
    return getFlashCardsIconBg(theme, currentTheme);
  }

  // Helper function to get card background
  const getCardBackground = (iconBg) => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark; 
    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
      
    };
  };
  const getNavBackground = () => {
    return currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  function onNext() {
    const nextIndex = activeIndex + 1;
    if (cards.length > nextIndex) {
      setActiveIndex(nextIndex);
    }
  }

  function onPrevious() {
    const nextIndex = activeIndex - 1;
    if (nextIndex >= 0) {
      setActiveIndex(nextIndex);
    }
  }

  function onBookmark() {
    if (cards.length > activeIndex) {
      const card = cards[activeIndex];
      if (isBookmark) {
        removeFlashcardFromBookmark(card.id);
      } else {
        addFlashcardToBookmark(card.id);
      }
    }
  }

  useEffect(() => {
    if (set_id && cards.length > activeIndex) {
      const card = cards[activeIndex];
      addFlashcardProgress(set_id, card.id);
    }
  }, [activeIndex]);

  function checkBookmark() {
    if (cards.length > activeIndex) {
      const card = cards[activeIndex];
      return profile?.flashcard_bookmarks?.includes(card.id) ?? false;
    }
    return false;
  }

  function onSnap(index) {
    setActiveIndex(index);
  }
  
  const isBookmark = checkBookmark();

  useEffect(() => {
    setSelectedIndex(null);
  }, [activeIndex]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          className="rounded-lg border-2 p-2 mr-4 flex items-center justify-center"
          style={{ borderColor: currentTheme === "light" ? theme?.border_color?.light : theme?.border_color?.dark }}
          onClick={() => navigate(-1)}
        >
          <BackArrowIcon
            color={currentTheme === "light" ? theme?.text?.light : theme?.text?.dark}
          />
        </button>
        <h1 
          className="text-2xl font-semibold"
          style={{ color: getTextColor() }}
        >
          {title}
        </h1>
      </div>

      {title !== "Bookmark" || cards?.length > 0 ? (
        <>
          {/* Card Navigation and Bookmark */}
          <div className="flex items-center justify-between mb-4">
            <div 
              className="flex items-center px-4 py-2 rounded-lg"
              // style={{ color: getTextColor(), backgroundColor: getNavBackground() }}
              style={{ 
                backgroundColor:  currentTheme == 'light' ? getNavBackground() : getIconBg(),
                color: currentTheme == 'light' ? getIconBg() : getTextColor(),
              }}>
              <FontAwesomeIcon icon={faBook} className="mr-2" style={{  color: currentTheme == 'light' ? getIconBg() : getTextColor() }}/>
              <span className="font-medium" style={{  color: currentTheme == 'light' ? getIconBg() : getTextColor() }}>
                {`${STRINGS.flashcard} ${activeIndex + 1}`}
              </span>
              <span className="text-sm ml-1 text-black dark:text-white">
                / {cards.length}
              </span>
            </div>
            
            <button
              className="p-2 rounded-lg transition-colors"
              onClick={onBookmark}
              style={{ 
                backgroundColor: 'transparent',
                color: getBookMarkColor(currentTheme,isBookmark)
              }}
            >
              <FontAwesomeIcon 
                icon={faBookmark} 
                className={isBookmark ? 'text-2xl' : 'text-2xl text-gray-400'} 
              />
            </button>
          </div>

          {/* Flashcard Container */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm mb-6"
            whileHover="hover"
            variants={cardVariants}
          >
            <div style={getCardBackground(getIconBg())}>
              <div className="p-6">
                <FlashCardTest
                  item={cards[activeIndex]}
                  isVisible={selectedIndex == activeIndex}
                  onViewMore={() => {
                    setSelectedIndex(activeIndex);
                  }}
                  title={title}
                />
              </div>
            </div>
          </motion.div>

          {/* Navigation Controls */}
          <NavBar 
            theme={theme}
            currentTheme={currentTheme}
            onNext={onNext}
            onPrevious={onPrevious}
            iconBg={getIconBg()}
            currentIndex={activeIndex}
            dataSet={cards?.length ?? 0}
            navTitle="Flashcard"
            showEnd={false}
            onEndTest={() => {}}
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-[50vh]">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
          >
            <FontAwesomeIcon 
              icon={faExclamationCircle} 
              className="text-4xl text-gray-400" 
            />
          </div>
          <p 
            className="text-lg text-gray-400 text-center"
          >
            {STRINGS.no_question_boomark}
          </p>
        </div>
      )}
    </div>
  );
};

export default QuickSets;